import {
  Box,
  CardActionArea,
  FormControlLabel,
  Radio,
  Stack,
} from '@mui/material';
// @mui
import { alpha, styled } from '@mui/material/styles';

// ----------------------------------------------------------------------

export const StyledWrap = styled(Box)(() => ({
  display: 'grid',
  gap: 8,
  gridTemplateColumns: 'repeat(2, 1fr)',
}));

// ----------------------------------------------------------------------

type StyledCardProps = {
  selected: boolean;
};

export const StyledCard = styled(CardActionArea, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<StyledCardProps>(({ selected, theme }) => ({
  '& .svg-color': {
    height: 28,
    width: 28,
  },
  alignItems: 'center',
  border: `solid 1px ${alpha(theme.palette.grey[500], 0.12)}`,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.disabled,
  display: 'flex',
  height: 72,
  justifyContent: 'center',
  ...(selected && {
    borderColor: alpha(theme.palette.grey[500], 0.24),
    boxShadow: theme.customShadows.z12,
    color: theme.palette.primary.main,
  }),
}));

// ----------------------------------------------------------------------

type StyledCircleColorProps = {
  color: string;
  selected: boolean;
};

export const StyledCircleColor = styled('div', {
  shouldForwardProp: (prop) => prop !== 'selected',
})<StyledCircleColorProps>(({ color, selected, theme }) => ({
  backgroundColor: color,
  borderRadius: '50%',
  height: 12,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: 12,
  ...(selected && {
    boxShadow: `-2px 4px 8px 0px ${alpha(color, 0.48)}`,
    height: 24,
    width: 24,
  }),
}));

// ----------------------------------------------------------------------

type MaskControlProps = {
  value: string;
};

export const MaskControl = ({ value }: MaskControlProps) => {
  return (
    <FormControlLabel
      control={<Radio sx={{ display: 'none' }} />}
      label=""
      sx={{
        bottom: 0,
        left: 0,
        m: 0,
        position: 'absolute',
        right: 0,
        top: 0,
      }}
      value={value}
    />
  );
};

// ----------------------------------------------------------------------

type LayoutIconValue = {
  layout: 'horizontal' | 'mini' | 'vertical';
};

export const LayoutIcon = ({ layout }: LayoutIconValue) => {
  const WIDTH = 16;

  const HEIGHT = 10;

  const SPACING = 0.5;

  const RADIUS = 0.5;

  const isNavHorizontal = layout === 'horizontal';

  const isNavMini = layout === 'mini';

  const styles = {
    borderRadius: RADIUS,
    height: 1,
    position: 'absolute',
    width: 1,
  };

  return (
    <Stack
      direction={layout === 'horizontal' ? 'column' : 'row'}
      sx={{ height: 1, width: 1 }}
    >
      <Box
        sx={{
          bgcolor: 'currentColor',
          borderRadius: RADIUS,
          mr: SPACING,
          opacity: 0.72,
          width: WIDTH,
          ...(isNavHorizontal && {
            borderRadius: RADIUS / 1.5,
            height: HEIGHT,
            mb: SPACING,
            width: 1,
          }),
          ...(isNavMini && {
            borderRadius: RADIUS / 2,
            width: WIDTH / 2,
          }),
        }}
      />

      <Box sx={{ borderRadius: RADIUS, flexGrow: 1, position: 'relative' }}>
        <Box sx={{ ...styles, bgcolor: 'currentColor', opacity: 0.16 }} />
        <Box
          sx={{ ...styles, border: `dashed 1px currentColor`, opacity: 0.48 }}
        />
      </Box>
    </Stack>
  );
};
